import { useEffect, useRef, useState } from 'react';
import { CircleCheck } from 'lucide-react';
import { Banner, Button, Card, EmptyState, Input, LoadingRegion } from '@/components/ds-dlp';
import { AuthLayout } from '@/app/AuthLayout';
import { BRAND } from '@/brand';

// API base path is fixed — nginx proxies /api → api container.
const API_BASE = '/api';

interface SetupPageProps {
  onComplete: () => void;
}

// Organisation IDs: a letter, then 1–29 lower-case letters, digits or
// underscores (go-api tenancy.ValidateSlug).
const ORG_SLUG = /^[a-z][a-z0-9_]{1,29}$/;

/** Suggests an organisation ID from its name: "Acme Corp." → "acme_corp". */
export function suggestOrgSlug(name: string): string {
  let slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  if (slug && !/^[a-z]/.test(slug)) slug = `org_${slug}`;
  return slug.slice(0, 30).replace(/_+$/, '');
}

/**
 * First-run setup, shown while the backend reports the system as not
 * initialised. On an empty installation it creates the organisation and its
 * first administrator in one step; when an organisation already exists (made
 * on the command line) it creates just the administrator. The setup secret
 * (TENANT_BOOTSTRAP_SECRET) is asked for only when the operator set one; the
 * server refuses setup once any administrator exists. On the d2d design system
 * (cascade plan 3.5), in AuthLayout's frame like sign-in.
 */
const SetupPage: React.FC<SetupPageProps> = ({ onComplete }) => {
  const [needsOrg, setNeedsOrg] = useState<boolean | null>(null);
  // The operator may set a setup secret (TENANT_BOOTSTRAP_SECRET); only then
  // is it asked for.
  const [secretRequired, setSecretRequired] = useState(false);
  const [orgName, setOrgName] = useState('');
  const [orgSlug, setOrgSlug] = useState('');
  const [slugEdited, setSlugEdited] = useState(false);
  const [askTenantSlug, setAskTenantSlug] = useState(false);
  const [tenantSlug, setTenantSlug] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [setupSecret, setSetupSecret] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // The status check runs once; a ref keeps the latest callback without
  // re-running it whenever the parent re-renders.
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/auth/setup/status`)
      .then((r): Promise<{ initialized?: boolean; needs_organization?: boolean; secret_required?: boolean }> => (r.ok ? r.json() : Promise.resolve({})))
      .then((d) => {
        if (cancelled) return;
        // Set up meanwhile (another tab, or the command line): leave this
        // form for sign-in rather than showing it stale.
        if (d.initialized === true) {
          onCompleteRef.current();
          return;
        }
        setSecretRequired(d.secret_required === true);
        setNeedsOrg(d.needs_organization === true);
      })
      .catch(() => { if (!cancelled) setNeedsOrg(false); });
    return () => { cancelled = true; };
  }, []);

  const onOrgName = (value: string) => {
    setOrgName(value);
    if (!slugEdited) setOrgSlug(suggestOrgSlug(value));
  };

  const handleSetup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (needsOrg) {
      if (!orgName.trim()) { setError('Enter the organisation name'); return; }
      if (!ORG_SLUG.test(orgSlug)) { setError('The organisation ID must start with a letter and use 2 to 30 lower-case letters, digits or underscores'); return; }
    }
    if (password !== confirmPassword) { setError('Passwords do not match'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }

    const body: Record<string, string> = { email, password, first_name: firstName, last_name: lastName };
    if (needsOrg) {
      body.org_name = orgName.trim();
      body.org_slug = orgSlug;
    } else if (tenantSlug.trim()) {
      body.tenant_slug = tenantSlug.trim().toLowerCase();
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/auth/setup`, {
        method: 'POST',
        headers: secretRequired
          ? { 'Content-Type': 'application/json', 'X-Bootstrap-Secret': setupSecret }
          : { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        localStorage.setItem('dsshield_token', data.token);
        localStorage.setItem('dsshield_refresh_token', data.refresh_token);
        if (data.tenant_slug) localStorage.setItem('dsshield_tenant', data.tenant_slug);
        setSuccess(true);
        return;
      }
      switch (data.code) {
        case 'ALREADY_INITIALIZED':
          // Someone finished setup first: go to sign-in.
          onComplete();
          return;
        case 'SETUP_SECRET_INVALID':
          setSecretRequired(true);
          break;
        case 'ORGANIZATION_REQUIRED':
          setNeedsOrg(true);
          break;
        case 'TENANT_SLUG_REQUIRED':
          setAskTenantSlug(true);
          break;
      }
      setError(data.error || 'Setup failed');
    } catch {
      setError('Network error. Check your connection.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <AuthLayout>
        <Card>
          <Card.Body>
            <EmptyState
              icon={<CircleCheck />}
              title="Setup complete"
              description={needsOrg
                ? 'Your organisation and administrator account have been created. Continue to finish setting up, starting with the licence if this installation needs one.'
                : 'Your administrator account has been created.'}
              action={<Button onClick={onComplete}>Continue</Button>}
              headingLevel={2}
            />
          </Card.Body>
        </Card>
      </AuthLayout>
    );
  }

  if (needsOrg === null) {
    return (
      <AuthLayout>
        <LoadingRegion lines={4} label="Checking this installation" />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout subtitle={needsOrg
      ? <>Welcome to {BRAND.name}. Create your organisation and its administrator to get started.</>
      : <>Welcome to {BRAND.name}. Create your administrator account to get started.</>}>
      <Card>
        <Card.Body>
          <form onSubmit={handleSetup} className="flex flex-col gap-4">
            {error ? <Banner variant="danger">{error}</Banner> : null}
            {needsOrg ? (
              <>
                <Input id="setup-org-name" label="Organisation name" required value={orgName} onChange={(e) => onOrgName(e.target.value)} autoComplete="organization" />
                <Input
                  id="setup-org-slug"
                  label="Organisation ID"
                  required
                  value={orgSlug}
                  onChange={(e) => { setSlugEdited(true); setOrgSlug(e.target.value.toLowerCase()); }}
                  help="Used to sign in. Starts with a letter; lower-case letters, digits and underscores."
                  autoComplete="off"
                  spellCheck={false}
                />
              </>
            ) : askTenantSlug ? (
              <Input
                id="setup-tenant-slug"
                label="Organisation ID"
                required
                value={tenantSlug}
                onChange={(e) => setTenantSlug(e.target.value)}
                help="The ID your organisation was created with."
                autoComplete="off"
                spellCheck={false}
              />
            ) : null}
            <div className="grid gap-4 sm:grid-cols-2">
              <Input id="setup-first-name" label="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} autoComplete="given-name" />
              <Input id="setup-last-name" label="Last name" value={lastName} onChange={(e) => setLastName(e.target.value)} autoComplete="family-name" />
            </div>
            <Input id="setup-email" label="Email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
            <Input
              id="setup-password"
              label="Password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              help="At least 8 characters."
              autoComplete="new-password"
            />
            <Input
              id="setup-confirm"
              label="Confirm password"
              type="password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
            />
            {secretRequired ? (
              <Input
                id="setup-secret"
                label="Setup secret"
                type="password"
                required
                value={setupSecret}
                onChange={(e) => setSetupSecret(e.target.value)}
                help="The TENANT_BOOTSTRAP_SECRET set on the server by your operator."
                autoComplete="off"
              />
            ) : null}
            <Button type="submit" loading={loading} disabled={loading} className="mt-1 w-full">
              {needsOrg ? 'Create organisation and administrator' : 'Create admin account'}
            </Button>
          </form>
        </Card.Body>
      </Card>
    </AuthLayout>
  );
};

export default SetupPage;
